import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Save, Trash2, Undo2 } from "lucide-react";
import {
  COPOPT_SCHEDULE_DAYS_DEFAULT,
  DEICE_DEFAULT_MIN,
  icaoCodeForStand,
  icaoCodeForWingspan,
  isJet,
  PLAN_AHEAD_DAYS_DEFAULT,
  type AircraftType,
  type AirportConfig,
  type CounterFunction,
  type OutboundBelt,
  type Sector,
  type SecurityConfig,
  type Settings,
  type Stand,
  type StandPreference,
} from "@yb/core";
import { api, ApiError } from "../lib/api";
import { AirlineMark } from "./AirlineMark";
import { ApiSection, DataSourcesSection, TextingSection, UsageSection } from "./IntegrationSettings";
import { SecuritySection } from "./Mfa";
import { COUNTER_FUNCTIONS, counterFunctionLabel, counterFunctionShort, stamp } from "../lib/format";
import { SettingsDiffView } from "./Changes";
import { ExportPlanForm } from "./PlanPrint";
import { DateTime } from "luxon";

export type Section = "general" | "stands" | "rules" | "counters" | "reclaim" | "outbound" | "airlines" | "sources" | "texting" | "api" | "security" | "history" | "export" | "usage";

export const SETTINGS_SECTIONS: Array<[Section, string, string]> = [
  ["general", "General & timings", "Buffers, check-in and belt timing, automation"],
  ["stands", "Stands & gates", "Size limits, jet bridges, customs gates"],
  ["rules", "Allocation rules", "Airline preferences and stand dependencies"],
  ["counters", "Check-in counters", "Common use and dedicated counters"],
  ["reclaim", "Reclaim belts", "Arrival baggage belts"],
  ["outbound", "Outbound belts", "Departure make-up belts"],
  ["airlines", "Airlines & aircraft", "Codes, colours, wingspans, turn times"],
  ["sources", "Data sources", "Loads from sheets and emails, BPSS2"],
  ["texting", "Texting & auto-fix", "Who is texted about conflicts"],
  ["api", "Integrations & API", "API keys, webhooks, Apron, docs"],
  ["security", "Sign-in & users", "Two-step sign-in, recovery codes, resets"],
  ["history", "Change history", "Who changed what, and when"],
  ["export", "Export PDF", "The plan for a day or a week, to print or send"],
  ["usage", "Usage", "Who is using the app, and from where"],
];

const input = "rounded-md border border-line bg-panel px-2 py-1 text-[12.5px] disabled:opacity-70";
const wide = "w-full";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1 align-middle";

function Num({ value, onChange, disabled, step = 1 }: { value: number | undefined; onChange: (v: number) => void; disabled: boolean; step?: number }) {
  return (
    <input
      type="number"
      step={step}
      className={clsx(input, "tabular w-24")}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    />
  );
}

/** A number that may be left empty, meaning "use the airport's own setting". */
function MinutesOrDefault({
  value,
  fallback,
  onChange,
  disabled,
  max = 720,
  label,
}: {
  value: number | undefined;
  fallback: number;
  onChange: (v: number | null) => void;
  disabled: boolean;
  /** The server's upper bound for this field (settings-schema.ts), so a save is never refused for a slip. */
  max?: number;
  label?: string;
}) {
  return (
    <input
      type="number"
      min={0}
      max={max}
      aria-label={label}
      className={clsx(input, "tabular w-20")}
      placeholder={String(fallback)}
      title={value == null ? `Using the airport default, ${fallback} min` : undefined}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, Math.min(max, Number(e.target.value))))}
    />
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-4 border-b border-line py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold">{label}</div>
        {hint && <div className="text-[11.5px] text-ink-3">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

/** A whole number held inside the range the server accepts, so a save is never refused for a slip of the keyboard. */
function Bounded({ value, min, max, onChange, disabled, label }: { value: number; min: number; max: number; onChange: (v: number) => void; disabled: boolean; label: string }) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      aria-label={label}
      className={clsx(input, "tabular w-24")}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Math.max(min, Math.min(max, Math.round(Number(e.target.value) || min))))}
    />
  );
}

/**
 * The checkpoint figures the security forecast plans on. Its own component, with the
 * values and the setter passed in, so the guide's "enter it in Settings" can be checked
 * against something that renders. The ranges are the server's (settings-schema.ts).
 */
export function SecurityCheckpointFields({ security, disabled, onChange }: { security: SecurityConfig | undefined; disabled: boolean; onChange: (next: SecurityConfig) => void }) {
  // An airport saved before the checkpoint existed has no block at all; start it from what
  // the forecast assumes in that case, so the first edit does not have to invent the rest.
  const cur: SecurityConfig = security ?? { checkpoint: "PBS", lanes: 4 };
  const patch = (p: Partial<SecurityConfig>) => onChange({ ...cur, ...p });
  return (
    <>
      <h3 className="mt-6 mb-1 text-[14px] font-bold">Security checkpoint</h3>
      <Field label="Checkpoint" hint="The name the screening feed reports it under">
        <input
          className={clsx(input, "w-24")}
          aria-label="Checkpoint"
          maxLength={20}
          value={cur.checkpoint}
          disabled={disabled}
          onChange={(e) => patch({ checkpoint: e.target.value })}
        />
      </Field>
      <Field label="Lanes" hint="How many lanes the checkpoint physically has">
        <Bounded label="Lanes" value={cur.lanes} min={1} max={40} disabled={disabled} onChange={(v) => patch({ lanes: v })} />
      </Field>
      <Field label="Passengers per lane per hour" hint="The security manager's figure. Empty: the forecast assumes 150 and says so">
        <input
          type="number"
          min={20}
          max={600}
          aria-label="Passengers per lane per hour"
          className={clsx(input, "tabular w-24")}
          placeholder="150"
          value={cur.perLaneHour ?? ""}
          disabled={disabled}
          onChange={(e) => patch({ perLaneHour: e.target.value === "" ? null : Math.max(20, Math.min(600, Math.round(Number(e.target.value) || 20))) })}
        />
      </Field>
      <Field label="Target wait" hint="Minutes. A quarter hour's lanes are the number that clears its passengers inside this">
        <Bounded label="Target wait" value={cur.targetWaitMin ?? 15} min={1} max={120} disabled={disabled} onChange={(v) => patch({ targetWaitMin: v })} />
      </Field>
    </>
  );
}

/**
 * The airlines the app knows: codes, name, colour, logo, default type and counter habits.
 * Its own component, with the draft and the setter passed in, so what an administrator can
 * set here can be checked against something that renders.
 */
export function AirlinesTable({ config: d, disabled: ro, onChange: set }: { config: AirportConfig; disabled: boolean; onChange: (fn: (c: AirportConfig) => void) => void }) {
  return (
    <table className="mb-8 w-full max-w-5xl text-[12.5px]">
      <thead>
        <tr className="border-b border-line">
          <th className={th}>ICAO</th>
          <th className={th}>IATA</th>
          <th className={th}>Name</th>
          <th className={th}>Colour</th>
          <th className={th} title="An https link to the airline's logo. Empty: pages show the code on the airline's colour instead.">
            Logo
          </th>
          <th className={th}>Default aircraft</th>
          <th className={th} title="Counters opened per departure, in counter order. Empty = by seats: Check-in, then Bag drop, then Priority.">
            Counter layout
          </th>
          <th className={th} title="Minutes before departure that this airline's check-in opens. Empty = the airport default in General & timings.">
            Opens
          </th>
          <th className={th} title="Minutes before departure that it closes. Empty = the airport default. 0 keeps the desks until the flight goes.">
            Closes
          </th>
          <th
            className={th}
            title="US (transborder) departures checked in apart from domestic: what the counters are signed, e.g. WS-USA (the priority desk reads WS-USA-PRIORITY), and the counters per flight. They go on common-use counters, not the airline's own. Empty sign = checked in like the airline's other flights."
          >
            US check-in
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.values(d.airlines).map((a) => (
          <tr key={a.code} className="border-b border-line">
            <td className={clsx(td, "font-semibold")}>{a.code}</td>
            <td className={td}>{a.iata}</td>
            <td className={td}>
              <input className={clsx(input, wide)} aria-label={`${a.code} name`} value={a.name} disabled={ro} onChange={(e) => set((c) => (c.airlines[a.code]!.name = e.target.value))} />
            </td>
            <td className={td}>
              <input type="color" aria-label={`${a.code} colour`} value={a.color} disabled={ro} onChange={(e) => set((c) => (c.airlines[a.code]!.color = e.target.value))} />
            </td>
            <td className={td}>
              <div className="flex items-center gap-1.5">
                {/* Keyed on the link so a corrected link is tried afresh rather than kept as broken. */}
                {a.logoUrl && <AirlineMark key={a.logoUrl} code={a.code} config={d} />}
                <input
                  type="url"
                  className={clsx(input, "w-44")}
                  aria-label={`${a.code} logo`}
                  placeholder="https://... (optional)"
                  maxLength={300}
                  value={a.logoUrl ?? ""}
                  disabled={ro}
                  onChange={(e) =>
                    set((c) => {
                      // An emptied box takes the field out rather than saving an empty string.
                      const v = e.target.value.trim();
                      if (v) c.airlines[a.code]!.logoUrl = v;
                      else delete c.airlines[a.code]!.logoUrl;
                    })
                  }
                />
              </div>
            </td>
            <td className={td}>
              <select
                className={clsx(input, wide)}
                aria-label={`${a.code} default aircraft`}
                value={d.defaultTypeByAirline[a.code] ?? ""}
                disabled={ro}
                onChange={(e) => set((c) => (c.defaultTypeByAirline[a.code] = e.target.value))}
              >
                <option value="">—</option>
                {Object.keys(d.aircraftTypes)
                  .sort()
                  .map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
              </select>
            </td>
            <td className={td}>
              <CounterLayout
                value={a.counterLayout ?? []}
                disabled={ro}
                onChange={(v) =>
                  set((c) => {
                    if (v.length) c.airlines[a.code]!.counterLayout = v;
                    else delete c.airlines[a.code]!.counterLayout;
                  })
                }
              />
            </td>
            <td className={td}>
              <MinutesOrDefault
                value={a.counterOpenBeforeMin}
                fallback={d.settings.counterOpenBeforeMin}
                disabled={ro}
                onChange={(v) =>
                  set((c) => {
                    if (v == null) delete c.airlines[a.code]!.counterOpenBeforeMin;
                    else c.airlines[a.code]!.counterOpenBeforeMin = v;
                  })
                }
              />
            </td>
            <td className={td}>
              <MinutesOrDefault
                value={a.counterCloseBeforeMin}
                fallback={d.settings.counterCloseBeforeMin}
                disabled={ro}
                onChange={(v) =>
                  set((c) => {
                    if (v == null) delete c.airlines[a.code]!.counterCloseBeforeMin;
                    else c.airlines[a.code]!.counterCloseBeforeMin = v;
                  })
                }
              />
            </td>
            <td className={td}>
              <div className="flex min-w-40 flex-col gap-1">
                <input
                  className={clsx(input, "w-28 font-mono uppercase")}
                  aria-label={`${a.code} US check-in sign`}
                  placeholder="e.g. WS-USA"
                  maxLength={24}
                  value={a.transborderCheckIn?.label ?? ""}
                  disabled={ro}
                  onChange={(e) =>
                    set((c) => {
                      // An emptied sign turns US check-in off: those flights check in like the rest.
                      const label = e.target.value.toUpperCase().replace(/\s+/g, "");
                      const air = c.airlines[a.code]!;
                      if (label) air.transborderCheckIn = { ...air.transborderCheckIn, label };
                      else delete air.transborderCheckIn;
                    })
                  }
                />
                {a.transborderCheckIn && (
                  <CounterLayout
                    value={a.transborderCheckIn.counterLayout ?? []}
                    disabled={ro}
                    onChange={(v) =>
                      set((c) => {
                        const t = c.airlines[a.code]!.transborderCheckIn!;
                        if (v.length) t.counterLayout = v;
                        else delete t.counterLayout;
                      })
                    }
                  />
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Check({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled: boolean; label?: string }) {
  return <input type="checkbox" aria-label={label} className="size-4 accent-[var(--color-brand)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
}

/**
 * The point costs behind the soft rules, every one the engine charges. The rules page
 * names this card as the place to change each of them, so each of them has to be here:
 * the oversize penalty was charged for years with nowhere on screen to see or set it.
 */
export function OptimizerWeightsFields({ settings, disabled, onChange }: { settings: Settings; disabled: boolean; onChange: (next: Settings) => void }) {
  const patch = (p: Partial<Settings>) => onChange({ ...settings, ...p });
  const weight = (label: string, value: number, apply: (v: number) => void, step = 1) => (
    <input type="number" step={step} min={0} aria-label={label} className={clsx(input, "tabular w-24")} value={value} disabled={disabled} onChange={(e) => apply(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))} />
  );
  return (
    <>
      <h3 className="mt-6 mb-1 text-[14px] font-bold">Optimizer weights</h3>
      <Field label="Remote stand penalty" hint="Cost of a remote or FBO stand, anything other than a contact stand">
        {weight("Remote stand penalty", settings.remotePenalty, (v) => patch({ remotePenalty: v }))}
      </Field>
      <Field label="International protection penalty" hint="Cost of a domestic flight on an international gate when one is due">
        {weight("International protection penalty", settings.intlProtectionPenalty, (v) => patch({ intlProtectionPenalty: v }))}
      </Field>
      <Field label="Fallback international gate penalty">
        {weight("Fallback international gate penalty", settings.intlFallbackPenalty, (v) => patch({ intlFallbackPenalty: v }))}
      </Field>
      <Field label="Tight gap penalty (per minute under the comfortable gap)">
        {weight("Tight gap penalty (per minute under the comfortable gap)", settings.tightBufferPenaltyPerMin, (v) => patch({ tightBufferPenaltyPerMin: v }), 0.5)}
      </Field>
      <Field label="Oversize penalty" hint="Cost of a small aircraft on a stand with more than 12 m of wingspan to spare, which wastes a large stand">
        {weight("Oversize penalty", settings.oversizePenalty, (v) => patch({ oversizePenalty: v }))}
      </Field>
      <Field label="Jet without a bridge penalty" hint="Cost of a jet, of any airline, on a stand with no jet bridge. Keep it above the airline and learned preferences together so jets go to bridges whenever one is free; 0 turns it off">
        {weight("Jet without a bridge penalty", settings.jetNoBridgePenalty ?? 0, (v) => patch({ jetNoBridgePenalty: v }))}
      </Field>
      <Field label="Pushback spacing (minutes)" hint="Departures from neighbouring stands closer together than this are flagged on the board. An alert only: it does not change which stand a flight gets. 0 turns it off">
        {weight("Pushback spacing (minutes)", settings.pushbackSpacingMin ?? 0, (v) => patch({ pushbackSpacingMin: Math.min(60, v) }))}
      </Field>
      <Field label="Customs walk (minutes)" hint="How long an international or transborder arrival's passengers walk past the other customs gates to the customs hall; 0 turns the rule off">
        {weight("Customs walk (minutes)", settings.customsClearMin ?? 30, (v) => patch({ customsClearMin: Math.min(120, v) }))}
      </Field>
      <Field label="Domestic arrival before it (minutes)" hint="A domestic arrival on another customs gate must be at least this much earlier than the international or transborder one">
        {weight("Domestic arrival before it (minutes)", settings.customsDomesticBeforeMin ?? 60, (v) => patch({ customsDomesticBeforeMin: Math.min(240, v) }))}
      </Field>
      <Field label="Boarding (minutes before departure)" hint="When a departure's passengers are at the gate; boarding on another customs gate during the walk is a clash">
        {weight("Boarding (minutes before departure)", settings.boardingMin ?? 30, (v) => patch({ boardingMin: Math.min(120, v) }))}
      </Field>
      <Field label="Customs walk penalty" hint="Cost of passengers meeting on the walk. Kept below an aircraft with no stand (5000), so a customs gate is still used when nothing else is free; each case is a conflict sent for approval">
        {weight("Customs walk penalty", settings.customsWalkPenalty ?? 0, (v) => patch({ customsWalkPenalty: Math.min(4999, v) }))}
      </Field>
      <Field label="Bridges to jets first penalty" hint="Cost of a turboprop on a jet bridge while a jet on the ground at the same time, which could have used that bridge, is on a stand without one. Jets first, then Q400s; 0 turns it off">
        {weight("Bridges to jets first penalty", settings.bridgePriorityPenalty ?? 0, (v) => patch({ bridgePriorityPenalty: v }))}
      </Field>
      <Field label="Long turboprop stay (minutes)" hint="A turboprop on the ground longer than this should not hold a jet bridge">
        {weight("Long turboprop stay (minutes)", settings.longPropOnBridgeMin ?? 90, (v) => patch({ longPropOnBridgeMin: Math.min(1440, v) }))}
      </Field>
      <Field label="Long turboprop stay on a bridge penalty" hint="Cost of a turboprop holding a jet bridge for longer than that; 0 turns it off">
        {weight("Long turboprop stay on a bridge penalty", settings.longPropOnBridgePenalty ?? 0, (v) => patch({ longPropOnBridgePenalty: v }))}
      </Field>
      <Field label="Long jet stay (minutes)" hint="A jet on the ground longer than this, other than a domestic overnight in from 16:00 and out before 10:00, belongs on a ground-load stand rather than holding a bridge">
        {weight("Long jet stay (minutes)", settings.jetLongStayMin ?? 180, (v) => patch({ jetLongStayMin: Math.min(1440, v) }))}
      </Field>
      <Field label="Long jet stay on a bridge penalty" hint="Cost of such a jet on a jet bridge; 0 turns it off">
        {weight("Long jet stay on a bridge penalty", settings.jetLongStayOnBridgePenalty ?? 150, (v) => patch({ jetLongStayOnBridgePenalty: v }))}
      </Field>
      <Field label="Small turboprops" hint="Type codes kept off jet bridge gates, separated by commas">
        <input
          aria-label="Small turboprops"
          className={clsx(input, "w-56")}
          disabled={disabled}
          defaultValue={(settings.smallPropTypes ?? ["B190", "BE20", "SF34", "SW4"]).join(", ")}
          onBlur={(e) => patch({ smallPropTypes: e.target.value.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean) })}
        />
      </Field>
      <Field label="Small turboprop on a bridge penalty" hint="Cost of one of those on a jet bridge gate. Above a remote stand (80), so they use the ground-load gates, Stand 5 or a remote stand first; 0 turns it off">
        {weight("Small turboprop on a bridge penalty", settings.smallPropOnBridgePenalty ?? 300, (v) => patch({ smallPropOnBridgePenalty: v }))}
      </Field>
      <Field label="Keep an airline together (per gate)" hint="Cost per gate between two aircraft of one of the airlines below on the ground at once. A tie-breaker; 0 turns it off">
        {weight("Keep an airline together (per gate)", settings.handlerSpreadPenalty ?? 0, (v) => patch({ handlerSpreadPenalty: v }))}
      </Field>
      <Field label="Airlines kept together" hint="ICAO codes, separated by commas. At YLW: Air Canada and WestJet">
        <input
          aria-label="Airlines kept together"
          className={clsx(input, "w-56")}
          disabled={disabled}
          defaultValue={(settings.keepTogetherAirlines ?? ["ACA", "WJA"]).join(", ")}
          onBlur={(e) => patch({ keepTogetherAirlines: e.target.value.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean) })}
        />
      </Field>
      <Field label="Keep a handler's aircraft close (per gate)" hint="Cost per gate between two aircraft of one ground handler on the ground at once (Airlines, Ground handlers). A very low priority; 0 turns it off">
        {weight("Keep a handler's aircraft close (per gate)", settings.handlerTogetherPenalty ?? 3, (v) => patch({ handlerTogetherPenalty: v }))}
      </Field>
      <Field label="Away from the handler's equipment penalty" hint="Cost of an aircraft on a stand away from where its ground handler keeps its equipment (Airlines, Ground handlers)">
        {weight("Away from the handler's equipment penalty", settings.handlerHomePenalty ?? 0, (v) => patch({ handlerHomePenalty: v }))}
      </Field>
    </>
  );
}

/**
 * Winter operations: whether departures hold their stand for de-icing, and for how long when
 * the aircraft type carries no figure of its own. Its own component, like the checkpoint
 * card, so a test can render it on its own. The range is the server's (settings-schema.ts).
 */
export function WinterOperationsFields({ settings, disabled, onChange }: { settings: Settings; disabled: boolean; onChange: (next: Settings) => void }) {
  const patch = (p: Partial<Settings>) => onChange({ ...settings, ...p });
  return (
    <>
      <h3 className="mt-6 mb-1 text-[14px] font-bold">Winter operations</h3>
      <Field label="De-icing on" hint="Departures hold their stand for de-icing before pushback">
        <Check label="De-icing on" checked={settings.deicingEnabled ?? false} disabled={disabled} onChange={(v) => patch({ deicingEnabled: v })} />
      </Field>
      <Field label="Minutes when the type has no figure" hint="Each aircraft type can carry its own figure under Airlines & aircraft; this is used where it does not">
        <Bounded
          label="Minutes when the type has no figure"
          value={settings.deiceDefaultMin ?? DEICE_DEFAULT_MIN}
          min={0}
          max={120}
          disabled={disabled}
          onChange={(v) => patch({ deiceDefaultMin: v })}
        />
      </Field>
    </>
  );
}

/**
 * Days ahead: how far CopOpt's schedule is read, and how far the plan-ahead job keeps the
 * days planned. Its own component so a test can render it. The ranges are the server's
 * (settings-schema.ts); an airport that never saved them gets the defaults.
 */
export function DaysAheadFields({ settings, disabled, onChange }: { settings: Settings; disabled: boolean; onChange: (next: Settings) => void }) {
  const patch = (p: Partial<Settings>) => onChange({ ...settings, ...p });
  return (
    <>
      <h3 className="mt-6 mb-1 text-[14px] font-bold">Days ahead</h3>
      <Field label="Days of schedule read from CopOpt" hint="From today, when CopOpt is connected. A day CopOpt publishes takes its flights from CopOpt only; past weekdays are used only for days it has not sent">
        <Bounded
          label="Days of schedule read from CopOpt"
          value={settings.copoptScheduleDays ?? COPOPT_SCHEDULE_DAYS_DEFAULT}
          min={1}
          max={14}
          disabled={disabled}
          onChange={(v) => patch({ copoptScheduleDays: v })}
        />
      </Field>
      <Field label="Plan ahead" hint="Days beyond today that are planned every hour, so every flight has a stand before the day is opened (0 = off)">
        <Bounded label="Plan ahead" value={settings.planAheadDays ?? PLAN_AHEAD_DAYS_DEFAULT} min={0} max={14} disabled={disabled} onChange={(v) => patch({ planAheadDays: v })} />
      </Field>
    </>
  );
}

/**
 * The aircraft types table: what each type needs of a stand and how long it holds one. Its
 * own component so the de-icing column can be checked by a test without the whole page.
 */
export function AircraftTypesTable({
  types,
  deiceDefaultMin,
  disabled,
  onChange,
}: {
  types: Record<string, AircraftType>;
  deiceDefaultMin: number;
  disabled: boolean;
  onChange: (code: string, fn: (t: AircraftType) => void) => void;
}) {
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="border-b border-line">
          <th className={th}>ICAO</th>
          <th className={th}>IATA</th>
          <th className={th}>Name</th>
          <th className={th}>Wingspan (m) / code</th>
          <th className={th}>Seats</th>
          <th className={th}>Min turn</th>
          <th className={th}>Target turn</th>
          <th className={th} title="Minutes a departure of this type holds its stand for de-icing while winter operations are on. Empty = the figure in General & timings.">
            De-ice min
          </th>
          <th className={th} title="Jets board by jet bridge where one is free; turboprops and pistons are ground-loaded">
            Engine
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.values(types)
          .sort((a, b) => a.code.localeCompare(b.code))
          .map((t) => (
            <tr key={t.code} className="border-b border-line">
              <td className={clsx(td, "font-semibold")}>{t.code}</td>
              <td className={td}>{t.iata ?? ""}</td>
              <td className={clsx(td, "text-ink-2")}>{t.name}</td>
              <td className={td}>
                <div className="flex items-center gap-1.5">
                  <Num value={t.wingspan} step={0.1} disabled={disabled} onChange={(v) => onChange(t.code, (x) => (x.wingspan = v))} />
                  <CodeLetter code={icaoCodeForWingspan(t.wingspan)} title="ICAO code letter" />
                </div>
              </td>
              <td className={td}>
                <Num value={t.seats} disabled={disabled} onChange={(v) => onChange(t.code, (x) => (x.seats = v))} />
              </td>
              <td className={td}>
                <Num value={t.minTurnMin} disabled={disabled} onChange={(v) => onChange(t.code, (x) => (x.minTurnMin = v))} />
              </td>
              <td className={td}>
                <Num value={t.targetTurnMin} disabled={disabled} onChange={(v) => onChange(t.code, (x) => (x.targetTurnMin = v))} />
              </td>
              <td className={td}>
                <MinutesOrDefault
                  label={`De-ice min ${t.code}`}
                  value={t.deiceMin}
                  fallback={deiceDefaultMin}
                  max={120}
                  disabled={disabled}
                  onChange={(v) =>
                    onChange(t.code, (x) => {
                      if (v == null) delete x.deiceMin;
                      else x.deiceMin = v;
                    })
                  }
                />
              </td>
              <td className={td}>
                <select
                  aria-label={`Engine ${t.code}`}
                  className={clsx(input, "w-28")}
                  value={t.engine ?? (isJet(t.code, { aircraftTypes: types } as AirportConfig) ? "jet" : "turboprop")}
                  disabled={disabled}
                  onChange={(e) => onChange(t.code, (x) => (x.engine = e.target.value as AircraftType["engine"]))}
                >
                  <option value="jet">Jet</option>
                  <option value="turboprop">Turboprop</option>
                  <option value="piston">Piston</option>
                </select>
              </td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

function AirlinePicker({ value, onChange, config, disabled }: { value: string[]; onChange: (v: string[]) => void; config: AirportConfig; disabled: boolean }) {
  return (
    <div className="flex flex-wrap gap-1">
      {Object.values(config.airlines).map((a) => {
        const on = value.includes(a.code);
        return (
          <button
            key={a.code}
            type="button"
            disabled={disabled}
            onClick={() => onChange(on ? value.filter((x) => x !== a.code) : [...value, a.code])}
            className={clsx("rounded-full border px-2 py-0.5 text-[11px] font-semibold", on ? "border-transparent text-white" : "border-line text-ink-3")}
            style={on ? { background: a.color } : undefined}
            title={a.name}
          >
            {a.iata}
          </button>
        );
      })}
    </div>
  );
}

/** The ground handlers: who works which airlines, and the stands its equipment is kept by. */
export function GroundHandlersTable({ config, disabled, onChange }: { config: AirportConfig; disabled: boolean; onChange: (fn: (c: AirportConfig) => void) => void }) {
  const handlers = config.groundHandlers ?? [];
  const stands = config.stands.filter((s) => s.active && s.kind === "contact").sort((a, b) => a.order - b.order);
  const update = (i: number, fn: (h: NonNullable<AirportConfig["groundHandlers"]>[number]) => void) =>
    onChange((c) => {
      c.groundHandlers = (c.groundHandlers ?? []).map((h) => ({ ...h, airlines: [...h.airlines], homeStands: [...h.homeStands] }));
      fn(c.groundHandlers[i]!);
    });
  // An airline has one handler: picking it for one takes it off any other.
  const takeAirlines = (i: number, airlines: string[]) =>
    onChange((c) => {
      c.groundHandlers = (c.groundHandlers ?? []).map((h, n) => (n === i ? { ...h, airlines } : { ...h, airlines: h.airlines.filter((a) => !airlines.includes(a)) }));
    });
  return (
    <div className="flex flex-col gap-2">
      {handlers.length === 0 && <p className="text-[12.5px] text-ink-3">No ground handlers yet.</p>}
      {handlers.map((h, i) => (
        <div key={h.id} className="rounded-lg border border-line p-3">
          <div className="mb-2 flex items-center gap-2">
            <input
              aria-label="Handler name"
              className={clsx(input, "w-56 font-semibold")}
              value={h.name}
              disabled={disabled}
              onChange={(e) => update(i, (x) => (x.name = e.target.value))}
            />
            {!disabled && (
              <button
                type="button"
                className="ml-auto rounded px-2 py-0.5 text-[11.5px] text-ink-3 hover:bg-bad/10 hover:text-bad"
                onClick={() => onChange((c) => (c.groundHandlers = (c.groundHandlers ?? []).filter((_, n) => n !== i)))}
              >
                Remove
              </button>
            )}
          </div>
          <div className="mb-1 text-[11px] tracking-wide text-ink-3 uppercase">Airlines it handles</div>
          <AirlinePicker value={h.airlines} onChange={(v) => takeAirlines(i, v)} config={config} disabled={disabled} />
          <div className="mt-2 mb-1 text-[11px] tracking-wide text-ink-3 uppercase">Equipment kept by</div>
          <div className="flex flex-wrap gap-1">
            {stands.map((s) => {
              const on = h.homeStands.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => update(i, (x) => (x.homeStands = on ? x.homeStands.filter((y) => y !== s.id) : [...x.homeStands, s.id]))}
                  className={clsx("rounded-full border px-2 py-0.5 text-[11px] font-semibold", on ? "border-transparent bg-info text-white" : "border-line text-ink-3")}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          className="self-start rounded-md border border-line px-2.5 py-1 text-[12px] font-semibold text-ink-2 hover:text-ink"
          onClick={() =>
            onChange((c) => {
              const n = (c.groundHandlers?.length ?? 0) + 1;
              c.groundHandlers = [...(c.groundHandlers ?? []), { id: `h${Date.now().toString(36)}`, name: `Handler ${n}`, airlines: [], homeStands: [] }];
            })
          }
        >
          Add a ground handler
        </button>
      )}
    </div>
  );
}

/** Domestic, transborder, international: none picked means any. */
function SectorChips({ value, onChange, disabled }: { value: Sector[]; onChange: (v: Sector[]) => void; disabled: boolean }) {
  const all: Array<[Sector, string]> = [
    ["D", "Domestic"],
    ["T", "Transborder"],
    ["I", "International"],
  ];
  return (
    <div className="flex flex-wrap gap-1">
      {all.map(([code, name]) => {
        const on = value.includes(code);
        return (
          <button
            key={code}
            type="button"
            disabled={disabled}
            title={name}
            onClick={() => onChange(on ? value.filter((x) => x !== code) : [...value, code])}
            className={clsx("rounded-full border px-2 py-0.5 text-[11px] font-semibold", on ? "border-ink bg-ink text-panel" : "border-line text-ink-3")}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

export function SettingsPage({
  canEdit,
  isSuper,
  onSaved,
  onRules,
  initialSection,
  planDate,
  sandbox = null,
}: {
  canEdit: boolean;
  isSuper: boolean;
  onSaved: () => void;
  onRules?: () => void;
  /** Opened from elsewhere in the app straight onto one section, such as the tow dialog onto Texting. */
  initialSection?: Section;
  /** The day on the board, where an export starts. */
  planDate?: string;
  /** The sandbox an administrator is working in, so an export prints that plan. */
  sandbox?: number | null;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const [draft, setDraft] = useState<AirportConfig | null>(null);
  const [section, setSection] = useState<Section>(initialSection ?? "general");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [openVersion, setOpenVersion] = useState<number | null>(null);
  const [message, setMessage] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);

  useEffect(() => {
    if (q.data && !draft) setDraft(structuredClone(q.data.config));
  }, [q.data, draft]);

  const dirty = useMemo(() => !!draft && !!q.data && JSON.stringify(draft) !== JSON.stringify(q.data.config), [draft, q.data]);

  if (!draft || !q.data) {
    return (
      <div className="grid flex-1 place-items-center">
        <Loader2 className="size-6 animate-spin text-ink-3" />
      </div>
    );
  }
  const d = draft;
  const ro = !canEdit;
  const set = (fn: (c: AirportConfig) => void) => {
    const next = structuredClone(d);
    fn(next);
    setDraft(next);
  };
  const s = d.settings;

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.saveSettings(d, note.trim() || "Updated settings");
      setNote("");
      await qc.invalidateQueries({ queryKey: ["settings"] });
      setDraft(null);
      setMessage({ tone: "ok", text: "Saved. The board has been re-planned with the new settings." });
      onSaved();
    } catch (err) {
      setMessage({ tone: "bad", text: err instanceof ApiError ? err.message : "Could not save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1">
      <nav className="w-60 shrink-0 border-r border-line bg-panel p-2">
        <div className="px-2 pt-1 pb-2 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Settings & rules</div>
        {/* Usage is for administrators; the server says so with a 403 to anyone else. */}
        {SETTINGS_SECTIONS.filter(([id]) => (id !== "usage" && id !== "export") || canEdit).map(([id, name, hint]) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={clsx("mb-0.5 block w-full rounded-lg px-2.5 py-2 text-left", section === id ? "bg-panel-2" : "hover:bg-panel-2/60")}
          >
            <div className="text-[13px] font-semibold">{name}</div>
            <div className="text-[11px] text-ink-3">{hint}</div>
          </button>
        ))}
        {!canEdit && <div className="mt-3 rounded-lg bg-panel-2 p-2.5 text-[11.5px] text-ink-3">Read-only. Only administrators can change settings.</div>}
        {q.data?.sandbox && (
          <div className="mt-3 rounded-lg border border-warn/50 bg-warn/15 p-2.5 text-[11.5px] text-warn">
            {`Sandbox: ${q.data.sandbox.name}. Changes saved here change the sandbox, not Live.`}
          </div>
        )}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="scroll-thin min-h-0 flex-1 overflow-auto p-5">
          <div className="mx-auto max-w-5xl">
            {section === "general" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">General & timings</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">How the optimizer spaces aircraft, when counters and belts open, and what it may change on its own.</p>
                <Field label="Minimum gap between aircraft on a stand" hint="Hard rule, in minutes">
                  <Num value={s.minBufferMin} disabled={ro} onChange={(v) => set((c) => (c.settings.minBufferMin = v))} />
                </Field>
                <Field label="Comfortable gap" hint="Smaller gaps are allowed but cost points">
                  <Num value={s.preferredBufferMin} disabled={ro} onChange={(v) => set((c) => (c.settings.preferredBufferMin = v))} />
                </Field>
                <Field label="Assumed ground time" hint="For an arrival with no linked departure (minutes)">
                  <Num value={s.defaultTurnMin} disabled={ro} onChange={(v) => set((c) => (c.settings.defaultTurnMin = v))} />
                </Field>
                <Field label="Overnight aircraft (RON)" hint="Keep them parked on stand, or tow them off after this many minutes on the ground (0 = stay parked)">
                  <Num value={s.towSplitMin} disabled={ro} onChange={(v) => set((c) => (c.settings.towSplitMin = v))} />
                </Field>
                <Field label="Keep international gates free" hint="Look-ahead for international / transborder flights (minutes)">
                  <Num value={s.intlLookaheadMin} disabled={ro} onChange={(v) => set((c) => (c.settings.intlLookaheadMin = v))} />
                </Field>
                <Field label="Check-in opens / closes before departure" hint="Minutes before STD">
                  <div className="flex gap-2">
                    <Num value={s.counterOpenBeforeMin} disabled={ro} onChange={(v) => set((c) => (c.settings.counterOpenBeforeMin = v))} />
                    <Num value={s.counterCloseBeforeMin} disabled={ro} onChange={(v) => set((c) => (c.settings.counterCloseBeforeMin = v))} />
                  </div>
                </Field>
                <Field label="Reclaim starts after on-block / runs for" hint="Minutes">
                  <div className="flex gap-2">
                    <Num value={s.beltStartAfterMin} disabled={ro} onChange={(v) => set((c) => (c.settings.beltStartAfterMin = v))} />
                    <Num value={s.beltDurationMin} disabled={ro} onChange={(v) => set((c) => (c.settings.beltDurationMin = v))} />
                  </div>
                </Field>
                <Field
                  label="Reclaim for international / transborder, and buffer"
                  hint="Minutes. Customs bags sit longer; the buffer is the gap before the belt counts as free again"
                >
                  <div className="flex gap-2">
                    <Num value={s.beltDurationIntlMin ?? s.beltDurationMin} disabled={ro} onChange={(v) => set((c) => (c.settings.beltDurationIntlMin = v))} />
                    <Num value={s.beltBufferMin ?? 0} disabled={ro} onChange={(v) => set((c) => (c.settings.beltBufferMin = v))} />
                  </div>
                </Field>
                <Field label="Outbound belt opens / closes before departure" hint="Minutes before STD">
                  <div className="flex gap-2">
                    <Num value={s.outboundOpenBeforeMin ?? 180} disabled={ro} onChange={(v) => set((c) => (c.settings.outboundOpenBeforeMin = v))} />
                    <Num value={s.outboundCloseBeforeMin ?? 20} disabled={ro} onChange={(v) => set((c) => (c.settings.outboundCloseBeforeMin = v))} />
                  </div>
                </Field>
                <WinterOperationsFields settings={s} disabled={ro} onChange={(next) => set((c) => (c.settings = next))} />
                <DaysAheadFields settings={s} disabled={ro} onChange={(next) => set((c) => (c.settings = next))} />
                <h3 className="mt-6 mb-1 text-[14px] font-bold">Automatic conflict handling</h3>
                <Field label="Fix conflicts automatically" hint="Conflicts further away than the window below are fixed without asking">
                  <Check checked={s.autoResolveEnabled ?? false} disabled={ro} onChange={(v) => set((c) => (c.settings.autoResolveEnabled = v))} />
                </Field>
                <Field label="Ask by text inside this window" hint="Conflicts closer than this many minutes are sent to approvers with numbered options">
                  <Num value={s.autoResolveWindowMin ?? 120} disabled={ro} onChange={(v) => set((c) => (c.settings.autoResolveWindowMin = v))} />
                </Field>
                <OptimizerWeightsFields settings={s} disabled={ro} onChange={(next) => set((c) => (c.settings = next))} />
                <SecurityCheckpointFields security={d.security} disabled={ro} onChange={(next) => set((c) => (c.security = next))} />
              </>
            )}

            {section === "stands" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Stands & gates</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">Inactive stands are hidden from the board and never used by the optimizer.</p>
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="border-b border-line">
                      <th className={th}>Active</th>
                      <th className={th}>Stand</th>
                      <th className={th}>Gate</th>
                      <th className={th}>Type</th>
                      <th className={th}>Jet bridge</th>
                      <th className={th}>Max wingspan (m) / code</th>
                      <th className={th} title="Leave empty for any airline">Airlines only</th>
                      <th className={th} title="Off: the optimizer never picks this stand, but a planner still can">Auto</th>
                      <th className={th}>For</th>
                      <th className={th}>Only</th>
                      <th className={th}>Intl only</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {d.stands.map((st, i) => {
                      const up = (fn: (x: Stand) => void) => set((c) => fn(c.stands[i]!));
                      return (
                        <tr key={i} className="border-b border-line">
                          <td className={td}>
                            <Check checked={st.active} disabled={ro} onChange={(v) => up((x) => (x.active = v))} />
                          </td>
                          <td className={td}>
                            <input className={clsx(input, wide)} value={st.name} disabled={ro} onChange={(e) => up((x) => (x.name = e.target.value))} />
                          </td>
                          <td className={td}>
                            <input className={clsx(input, wide)} value={st.gate ?? ""} disabled={ro} onChange={(e) => up((x) => (x.gate = e.target.value || null))} />
                          </td>
                          <td className={td}>
                            <select className={clsx(input, wide)} value={st.kind} disabled={ro} onChange={(e) => up((x) => (x.kind = e.target.value as Stand["kind"]))}>
                              <option value="contact">Contact</option>
                              <option value="remote">Remote</option>
                              <option value="fbo">FBO</option>
                            </select>
                          </td>
                          <td className={td}>
                            <Check checked={st.jetbridge} disabled={ro} onChange={(v) => up((x) => (x.jetbridge = v))} />
                          </td>
                          <td className={td}>
                            <div className="flex items-center gap-1.5">
                              <Num value={st.maxWingspan} step={0.1} disabled={ro} onChange={(v) => up((x) => (x.maxWingspan = v))} />
                              <CodeLetter code={icaoCodeForStand(st.maxWingspan)} title="Largest ICAO code this stand takes" />
                            </div>
                          </td>
                          <td className={td}>
                            <input
                              className={clsx(input, "w-28 uppercase")}
                              placeholder="any"
                              disabled={ro}
                              value={(st.allowedAirlines ?? []).join(" ")}
                              title="Airline codes, separated by spaces. Empty means any airline."
                              onChange={(e) =>
                                up((x) => {
                                  const list = e.target.value.toUpperCase().split(/[\s,]+/).filter(Boolean);
                                  if (list.length) x.allowedAirlines = list;
                                  else delete x.allowedAirlines;
                                })
                              }
                            />
                          </td>
                          <td className={td}>
                            <Check checked={st.autoAllocate !== false} disabled={ro} onChange={(v) => up((x) => (v ? delete x.autoAllocate : (x.autoAllocate = false)))} />
                          </td>
                          <td className={td}>
                            <select
                              className={clsx(input, wide)}
                              value={st.international ?? ""}
                              disabled={ro}
                              onChange={(e) => up((x) => (x.international = (e.target.value || null) as Stand["international"]))}
                            >
                              <option value="">Domestic only</option>
                              <option value="primary">International gate</option>
                              <option value="fallback">International fallback</option>
                            </select>
                          </td>
                          <td className={td}>
                            <Check checked={st.domesticExcluded} disabled={ro} onChange={(v) => up((x) => (x.domesticExcluded = v))} />
                          </td>
                          <td className={td}>
                            {canEdit && (
                              <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => set((c) => c.stands.splice(i, 1))} aria-label="Remove stand">
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {canEdit && (
                  <button
                    className="mt-3 flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                    onClick={() =>
                      set((c) =>
                        c.stands.push({
                          id: `Stand ${c.stands.length + 1}`,
                          name: `Stand ${c.stands.length + 1}`,
                          gate: null,
                          kind: "remote",
                          jetbridge: false,
                          maxWingspan: 36,
                          international: null,
                          domesticExcluded: false,
                          active: true,
                          order: c.stands.length + 1,
                        }),
                      )
                    }
                  >
                    <Plus className="size-4" /> Add stand
                  </button>
                )}
              </>
            )}

            {section === "rules" && (
              <>
                <div className="mb-1 flex flex-wrap items-baseline gap-3">
                  <h2 className="text-[18px] font-bold">Allocation rules</h2>
                  {onRules && (
                    <button className="text-[12.5px] font-semibold text-info hover:underline" onClick={onRules}>
                      Every rule the allocator uses, in one view →
                    </button>
                  )}
                </div>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  Preferences are soft: the optimizer tries to meet them and says why when it can&apos;t. Size, customs and closures are always hard rules.
                </p>

                <div className="mb-4 rounded-xl border border-line p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-[13px] font-bold">
                    Learned from past plans
                    <label className="ml-auto flex items-center gap-1.5 text-[12px] font-normal">
                      <Check checked={d.settings.learnFromHistory !== false} disabled={ro} onChange={(v) => set((c) => (c.settings.learnFromHistory = v))} /> Follow the usual
                      stands
                    </label>
                    <label className="flex items-center gap-1.5 text-[12px] font-normal" title="How much it costs to park somewhere other than the usual stand">
                      Strength <Num value={d.settings.learnedWeight ?? 40} disabled={ro} onChange={(v) => set((c) => (c.settings.learnedWeight = v))} />
                    </label>
                  </div>
                  <p className="text-[12px] text-ink-3">
                    Where flights were actually parked over the last 60 days. A flight counts as having a usual stand once it has been planned at least 3 times and sat there
                    for 60% of them. These are preferences, so size, customs and closures still win.
                  </p>
                  <div className="mt-2 space-y-1">
                    {(q.data?.learned ?? []).map((p) => (
                      <div key={p.id} className="text-[12px] text-ink-2">
                        {p.label}
                      </div>
                    ))}
                    {!q.data?.learned?.length && <div className="text-[12px] text-ink-3">Nothing learned yet: it needs a few days of plans.</div>}
                  </div>
                </div>
                <div className="space-y-2">
                  {d.preferences.map((p, i) => {
                    const up = (fn: (x: StandPreference) => void) => set((c) => fn(c.preferences[i]!));
                    const mode = p.prefer.jetbridge === true ? "jetbridge" : p.prefer.jetbridge === false ? "ground" : "stands";
                    // Which aircraft the rule is for: all of them, only some, or all but some.
                    // Read from whether the list exists, not whether it has anything in it yet:
                    // the moment "only these types" is chosen the list is empty, and judging by
                    // length snapped the card straight back to "any" with nothing to pick from.
                    const scope = p.aircraftTypes !== undefined ? "only" : p.exceptAircraftTypes !== undefined ? "except" : "any";
                    const scoped = scope === "only" ? (p.aircraftTypes ?? []) : scope === "except" ? (p.exceptAircraftTypes ?? []) : [];
                    const setScope = (s: "any" | "only" | "except", types: string[]) =>
                      up((x) => {
                        delete x.aircraftTypes;
                        delete x.exceptAircraftTypes;
                        if (s === "only") x.aircraftTypes = types;
                        if (s === "except") x.exceptAircraftTypes = types;
                      });
                    return (
                      <div key={i} className="rounded-xl border border-line p-3">
                        <div className="flex items-center gap-2">
                          <input className={clsx(input, "flex-1 font-semibold")} value={p.label} disabled={ro} onChange={(e) => up((x) => (x.label = e.target.value))} />
                          <span className="text-[11px] text-ink-3">Weight</span>
                          <Num value={p.weight} disabled={ro} onChange={(v) => up((x) => (x.weight = v))} />
                          {canEdit && (
                            <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => set((c) => c.preferences.splice(i, 1))} aria-label="Remove rule">
                              <Trash2 className="size-4" />
                            </button>
                          )}
                        </div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-[110px_1fr]">
                          <div className="text-[11.5px] text-ink-3">Airlines</div>
                          <AirlinePicker value={p.airlines} config={d} disabled={ro} onChange={(v) => up((x) => (x.airlines = v))} />
                          <div className="text-[11.5px] text-ink-3">Prefers</div>
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              className={clsx(input, "w-44")}
                              value={mode}
                              disabled={ro}
                              onChange={(e) =>
                                up(
                                  (x) =>
                                    (x.prefer =
                                      e.target.value === "jetbridge" ? { jetbridge: true } : e.target.value === "ground" ? { jetbridge: false } : { stands: x.prefer.stands ?? [] }),
                                )
                              }
                            >
                              <option value="jetbridge">Jet bridge stands</option>
                              <option value="ground">Ground-loaded stands (no bridge)</option>
                              <option value="stands">These stands</option>
                            </select>
                            {mode === "stands" &&
                              d.stands
                                .filter((st) => st.active)
                                .map((st) => {
                                  const on = p.prefer.stands?.includes(st.id) ?? false;
                                  return (
                                    <button
                                      key={st.id}
                                      type="button"
                                      disabled={ro}
                                      onClick={() =>
                                        up((x) => (x.prefer = { stands: on ? (x.prefer.stands ?? []).filter((z) => z !== st.id) : [...(x.prefer.stands ?? []), st.id] }))
                                      }
                                      className={clsx("rounded-full border px-2 py-0.5 text-[11px]", on ? "border-ink bg-ink text-panel" : "border-line text-ink-3")}
                                    >
                                      {st.name.replace("Stand ", "")}
                                    </button>
                                  );
                                })}
                          </div>
                          <div className="text-[11.5px] text-ink-3" title="A Q400 boards by stairs; its airline's 737 needs the bridge. One airline can carry a rule for each.">
                            Aircraft
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <select className={clsx(input, "w-44")} value={scope} disabled={ro} onChange={(e) => setScope(e.target.value as "any" | "only" | "except", scoped)}>
                              <option value="any">Any aircraft</option>
                              <option value="only">Only these types</option>
                              <option value="except">All except these</option>
                            </select>
                            {scope !== "any" && scoped.length === 0 && (
                              <span className="text-[11px] text-warn">Pick at least one type. With none picked this rule still applies to any aircraft.</span>
                            )}
                            {scope !== "any" &&
                              Object.values(d.aircraftTypes).map((at) => {
                                const on = scoped.includes(at.code);
                                return (
                                  <button
                                    key={at.code}
                                    type="button"
                                    disabled={ro}
                                    title={at.name}
                                    onClick={() => setScope(scope, on ? scoped.filter((z) => z !== at.code) : [...scoped, at.code])}
                                    className={clsx("rounded-full border px-2 py-0.5 text-[11px]", on ? "border-ink bg-ink text-panel" : "border-line text-ink-3")}
                                  >
                                    {at.code}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {canEdit && (
                  <button
                    className="mt-3 flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                    onClick={() =>
                      set((c) => c.preferences.push({ id: `pref-${Date.now().toString(36)}`, label: "New preference", airlines: [], prefer: { jetbridge: true }, weight: 40 }))
                    }
                  >
                    <Plus className="size-4" /> Add preference
                  </button>
                )}

                <h3 className="mt-8 mb-1 text-[14px] font-bold">Stand dependencies</h3>
                <p className="mb-2 text-[12px] text-ink-3">
                  When one stand is used by certain aircraft, or by flights of a certain kind, a neighbouring stand is blocked - for everything, or only for some flights. A 738 on 1
                  reaching into 2 is the first; an international flight on 4 closing 1 to 3 to domestic flights is the second.
                </p>
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="border-b border-line">
                      <th className={th}>When stand</th>
                      <th className={th}>is used by (types, empty = any)</th>
                      <th className={th}>flying (empty = any)</th>
                      <th className={th}>block stand</th>
                      <th className={th}>for (empty = all)</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {d.dependencies.map((dep, i) => (
                      <tr key={i} className="border-b border-line">
                        <td className={td}>
                          <select className={clsx(input, wide)} value={dep.stand} disabled={ro} onChange={(e) => set((c) => (c.dependencies[i]!.stand = e.target.value))}>
                            {d.stands.map((st) => (
                              <option key={st.id} value={st.id}>
                                {st.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={td}>
                          <input
                            className={clsx(input, wide)}
                            value={dep.whenTypes.join(", ")}
                            disabled={ro}
                            placeholder="e.g. B738, B38M"
                            onChange={(e) =>
                              set((c) => (c.dependencies[i]!.whenTypes = e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean)))
                            }
                          />
                        </td>
                        <td className={td}>
                          <SectorChips
                            value={dep.whenSectors ?? []}
                            disabled={ro}
                            onChange={(v) => set((c) => (v.length ? (c.dependencies[i]!.whenSectors = v) : delete c.dependencies[i]!.whenSectors))}
                          />
                        </td>
                        <td className={td}>
                          <select className={clsx(input, wide)} value={dep.blocks} disabled={ro} onChange={(e) => set((c) => (c.dependencies[i]!.blocks = e.target.value))}>
                            {d.stands.map((st) => (
                              <option key={st.id} value={st.id}>
                                {st.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={td}>
                          <SectorChips
                            value={dep.blocksSectors ?? []}
                            disabled={ro}
                            onChange={(v) => set((c) => (v.length ? (c.dependencies[i]!.blocksSectors = v) : delete c.dependencies[i]!.blocksSectors))}
                          />
                        </td>
                        <td className={td}>
                          {canEdit && (
                            <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => set((c) => c.dependencies.splice(i, 1))} aria-label="Remove">
                              <Trash2 className="size-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {canEdit && (
                  <button
                    className="mt-3 flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                    onClick={() => set((c) => c.dependencies.push({ stand: c.stands[0]!.id, blocks: c.stands[1]?.id ?? c.stands[0]!.id, whenTypes: [] }))}
                  >
                    <Plus className="size-4" /> Add dependency
                  </button>
                )}
              </>
            )}

            {section === "counters" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Check-in counters</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  Dedicated counters always belong to one airline. Common-use counters are shared out by the optimizer. Each flight&apos;s counters are split into Priority, Check-in and
                  Bag drop from the airline&apos;s counter layout (Airlines &amp; aircraft). A fixed use set here always wins.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {d.counters.map((ct, i) => (
                    <div key={ct.id} className="flex items-center gap-2 rounded-lg border border-line p-2">
                      <div className="w-20 shrink-0 text-[13px] font-semibold">Counter {ct.number}</div>
                      <select
                        className={clsx(input, wide)}
                        value={ct.dedicatedTo ?? ""}
                        disabled={ro}
                        onChange={(e) => set((c) => (c.counters[i]!.dedicatedTo = e.target.value || null))}
                      >
                        <option value="">Common use</option>
                        {Object.values(d.airlines).map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                      <select
                        className={clsx(input, wide)}
                        value={ct.function ?? ""}
                        disabled={ro}
                        title="Fixed use of this counter"
                        onChange={(e) => set((c) => (c.counters[i]!.function = (e.target.value || null) as CounterFunction | null))}
                      >
                        <option value="">Any use</option>
                        {COUNTER_FUNCTIONS.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div className="mt-3 flex gap-2">
                    <button
                      className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                      onClick={() =>
                        set((c) => {
                          const n = Math.max(0, ...c.counters.map((x) => x.number)) + 1;
                          c.counters.push({ id: `C${String(n).padStart(2, "0")}`, number: n, dedicatedTo: null });
                        })
                      }
                    >
                      <Plus className="size-4" /> Add counter
                    </button>
                    <button
                      className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                      onClick={() => set((c) => c.counters.pop())}
                    >
                      <Trash2 className="size-4" /> Remove last
                    </button>
                  </div>
                )}
              </>
            )}

            {section === "reclaim" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Reclaim belts</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  Arrival baggage belts. <b>Flights at once</b> is how many flights can be on a belt at the same time. <b>For</b> sets the sectors a belt is meant for:
                  it is kept clear while one of those flights is due and shared like any other belt when none is. <b>Only</b> makes that exclusive, for a belt inside
                  the customs hall where domestic bags cannot go. Timing is in General &amp; timings: international and transborder bags sit longer, and the buffer is
                  the gap before the belt counts as free again.
                </p>
                <table className="w-full max-w-xl text-[12.5px]">
                  <thead>
                    <tr className="border-b border-line">
                      <th className={th}>Name</th>
                      <th className={th}>Flights at once</th>
                      <th className={th}>For</th>
                      <th className={th}>Only</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {d.belts.map((b, i) => (
                      <tr key={b.id} className="border-b border-line">
                        <td className={td}>
                          <input className={clsx(input, wide)} value={b.name} disabled={ro} onChange={(e) => set((c) => (c.belts[i]!.name = e.target.value))} />
                        </td>
                        <td className={td}>
                          <Num value={b.capacity} disabled={ro} onChange={(v) => set((c) => (c.belts[i]!.capacity = Math.max(1, v)))} />
                        </td>
                        <td className={td}>
                          <div className="flex gap-1">
                            {(["D", "T", "I"] as const).map((sec) => {
                              const on = (b.sectors ?? (b.international ? ["I"] : [])).includes(sec);
                              return (
                                <button
                                  key={sec}
                                  type="button"
                                  disabled={ro}
                                  className={clsx("rounded px-1.5 py-0.5 text-[11px] font-semibold", on ? "bg-info/20 text-info" : "bg-panel-2 text-ink-3")}
                                  title={sec === "D" ? "Domestic" : sec === "T" ? "Transborder" : "International"}
                                  onClick={() =>
                                    set((c) => {
                                      const belt = c.belts[i]!;
                                      const now: Sector[] = belt.sectors ?? (belt.international ? ["I"] : []);
                                      belt.sectors = on ? now.filter((x) => x !== sec) : [...now, sec];
                                      delete belt.international;
                                    })
                                  }
                                >
                                  {sec}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td className={td}>
                          <input
                            type="checkbox"
                            className="size-4"
                            disabled={ro || !(b.sectors ?? []).length}
                            checked={b.exclusive ?? b.international === "only"}
                            title="Only those sectors may use this belt"
                            onChange={(e) =>
                              set((c) => {
                                c.belts[i]!.exclusive = e.target.checked;
                                delete c.belts[i]!.international;
                              })
                            }
                          />
                        </td>
                        <td className={td}>
                          {canEdit && (
                            <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => set((c) => c.belts.splice(i, 1))} aria-label="Remove belt">
                              <Trash2 className="size-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {canEdit && (
                  <button
                    className="mt-3 flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                    onClick={() => set((c) => c.belts.push({ id: `B${c.belts.length + 1}`, name: `Reclaim belt ${c.belts.length + 1}`, capacity: 2 }))}
                  >
                    <Plus className="size-4" /> Add reclaim belt
                  </button>
                )}
              </>
            )}

            {section === "outbound" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Outbound belts</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  Departure make-up belts. Leave airlines and sectors empty to allow everyone.
                </p>
                <div className="space-y-2">
                  {(d.outboundBelts ?? []).map((b, i) => {
                    const up = (fn: (x: OutboundBelt) => void) => set((c) => fn(c.outboundBelts![i]!));
                    return (
                      <div key={b.id} className="rounded-xl border border-line p-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <Check checked={b.active} disabled={ro} onChange={(v) => up((x) => (x.active = v))} />
                          <input className={clsx(input, "w-56 font-semibold")} value={b.name} disabled={ro} onChange={(e) => up((x) => (x.name = e.target.value))} />
                          <span className="text-[11.5px] text-ink-3">Flights at once</span>
                          <Num value={b.capacity} disabled={ro} onChange={(v) => up((x) => (x.capacity = Math.max(1, v)))} />
                          <div className="flex-1" />
                          {canEdit && (
                            <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => set((c) => c.outboundBelts!.splice(i, 1))} aria-label="Remove belt">
                              <Trash2 className="size-4" />
                            </button>
                          )}
                        </div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-[110px_1fr]">
                          <div className="text-[11.5px] text-ink-3">Airlines</div>
                          <AirlinePicker value={b.airlines} config={d} disabled={ro} onChange={(v) => up((x) => (x.airlines = v))} />
                          <div className="text-[11.5px] text-ink-3">Sectors</div>
                          <div className="flex gap-1">
                            {(["D", "T", "I"] as Sector[]).map((sec) => {
                              const on = b.sectors.includes(sec);
                              return (
                                <button
                                  key={sec}
                                  type="button"
                                  disabled={ro}
                                  onClick={() => up((x) => (x.sectors = on ? x.sectors.filter((z) => z !== sec) : [...x.sectors, sec]))}
                                  className={clsx("rounded-full border px-2 py-0.5 text-[11px]", on ? "border-ink bg-ink text-panel" : "border-line text-ink-3")}
                                >
                                  {sec === "D" ? "Domestic" : sec === "T" ? "Transborder" : "International"}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {canEdit && (
                  <button
                    className="mt-3 flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3"
                    onClick={() =>
                      set((c) => {
                        const n = (c.outboundBelts?.length ?? 0) + 1;
                        c.outboundBelts = [...(c.outboundBelts ?? []), { id: `OB${n}`, name: `Outbound belt ${n}`, capacity: 2, airlines: [], sectors: [], active: true }];
                      })
                    }
                  >
                    <Plus className="size-4" /> Add outbound belt
                  </button>
                )}
              </>
            )}

            {section === "airlines" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Airlines</h2>
                <AirlinesTable config={d} disabled={ro} onChange={set} />
                <h2 className="mt-6 mb-1 text-[18px] font-bold">Ground handlers</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  Who handles each airline, and the gates where each keeps its equipment. A handler with no airlines takes every airline the others do not name. The
                  planner keeps a handler&apos;s aircraft a little closer together (a very low priority) and near its equipment where gates are set. The weights are under
                  General, Optimizer weights.
                </p>
                <GroundHandlersTable config={d} disabled={ro} onChange={set} />
                <h2 className="mb-1 text-[18px] font-bold">Aircraft types</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  Wingspan decides which stands fit and sets the ICAO code (A &lt; 15 m, B &lt; 24 m, C &lt; 36 m, D &lt; 52 m, E &lt; 65 m, F &lt; 80 m). Turn times set the minimum and
                  target ground time. De-ice min is how long a departure holds its stand for de-icing while winter operations are on (General &amp; timings).
                </p>
                <AircraftTypesTable
                  types={d.aircraftTypes}
                  deiceDefaultMin={s.deiceDefaultMin ?? DEICE_DEFAULT_MIN}
                  disabled={ro}
                  onChange={(code, fn) => set((c) => fn(c.aircraftTypes[code]!))}
                />
              </>
            )}

            {section === "sources" && <DataSourcesSection draft={d} set={set} canEdit={canEdit} />}
            {section === "texting" && <TextingSection canEdit={canEdit} tz={d.timezone} />}
            {section === "usage" && <UsageSection tz={d.timezone} />}
            {section === "export" && canEdit && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Export PDF</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">
                  The plan as printed pages for the teams who work without the app open: stands and gates for the ramp, check-in counters, and reclaim and outbound belts.
                </p>
                <ExportPlanForm date={planDate ?? DateTime.now().setZone(d.timezone).toISODate()!} sandbox={sandbox} />
              </>
            )}
            {section === "api" && <ApiSection canEdit={canEdit} tz={d.timezone} />}

            {section === "security" && <SecuritySection isAdmin={canEdit} isSuper={isSuper} />}
            {section === "history" && (
              <>
                <h2 className="mb-1 text-[18px] font-bold">Change history</h2>
                <p className="mb-3 text-[12.5px] text-ink-3">Every saved version of the settings. Open one to see exactly which fields it changed against the version before it.</p>
                <div className="divide-y divide-line rounded-xl border border-line">
                  {q.data.history.map((h, i) => (
                    <div key={h.id} className="px-3 py-2 text-[12.5px]">
                      <div className="flex items-center gap-3">
                        <span className="tabular w-10 text-ink-3">#{h.id}</span>
                        <span className="flex-1">
                          {h.note} {i === 0 && <span className="ml-1 rounded bg-ok/15 px-1 text-[10px] font-bold text-ok">current</span>}
                        </span>
                        <span className="text-ink-3">{h.createdBy}</span>
                        <span className="tabular text-ink-3">{stamp(h.createdAt, d.timezone)}</span>
                        <button
                          className="rounded-md border border-line px-2 py-0.5 text-[12px] font-semibold hover:border-ink-3"
                          onClick={() => setOpenVersion(openVersion === h.id ? null : h.id)}
                          aria-expanded={openVersion === h.id}
                        >
                          {openVersion === h.id ? "Hide" : "What changed"}
                        </button>
                      </div>
                      {openVersion === h.id && (
                        <div className="mt-2 rounded-lg bg-panel-2/40 p-2">
                          <SettingsDiffView id={h.id} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {canEdit && (dirty || message) && (
          <div className="flex items-center gap-3 border-t border-line bg-panel px-5 py-3">
            {message && <span className={clsx("text-[12.5px] font-semibold", message.tone === "ok" ? "text-ok" : "text-bad")}>{message.text}</span>}
            {dirty && (
              <>
                <span className="text-[12.5px] text-warn">Unsaved changes</span>
                <input
                  className={clsx(input, "max-w-md flex-1")}
                  placeholder="What changed? (shown in the history)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  onClick={() => setDraft(structuredClone(q.data!.config))}
                  className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold"
                >
                  <Undo2 className="size-4" /> Discard
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-[12.5px] font-bold text-brand-ink disabled:opacity-60"
                >
                  {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CodeLetter({ code, title }: { code: string; title: string }) {
  return (
    <span className="rounded bg-ink-3/15 px-1.5 py-0.5 text-[11px] font-bold text-ink-2" title={title}>
      {code}
    </span>
  );
}

/** Ordered counter uses for an airline's departures, e.g. PRI CHK CHK BAG. */
function CounterLayout({ value, onChange, disabled }: { value: CounterFunction[]; onChange: (v: CounterFunction[]) => void; disabled: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.length === 0 && <span className="text-ink-3">Auto (by seats)</span>}
      {value.map((f, i) => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          title={disabled ? counterFunctionLabel(f) : `${counterFunctionLabel(f)}: click to remove`}
          onClick={() => onChange(value.filter((_, j) => j !== i))}
          className="rounded bg-ink-3/15 px-1.5 py-0.5 text-[10.5px] font-bold text-ink-2 enabled:hover:bg-bad/15 enabled:hover:text-bad"
        >
          {counterFunctionShort(f)}
        </button>
      ))}
      {!disabled && value.length < 12 && (
        <select
          className="rounded border border-line bg-panel px-1 py-0.5 text-[11px]"
          value=""
          aria-label="Add a counter"
          onChange={(e) => e.target.value && onChange([...value, e.target.value as CounterFunction])}
        >
          <option value="">+ add</option>
          {COUNTER_FUNCTIONS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
